import { ArrowUpRight, Ship } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite, waLink } from "@/context/SiteContext";

const DEFAULT_BOAT_IMG =
  "https://images.unsplash.com/photo-1638979455225-577905c5ada6?crop=entropy&cs=srgb&fm=jpg&q=85";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const TimeGroup = ({ label, times, testid }) => (
  <div data-testid={testid}>
    <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-seastone mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {times && times.length > 0 ? (
        times.map((t) => (
          <span key={t} className="font-mono text-[11px] bg-sand2 rounded-md px-2.5 py-1" data-testid={`boat-time-${t.replace(":", "")}`}>
            {t}
          </span>
        ))
      ) : (
        <span className="text-[11px] text-seastone italic">On request</span>
      )}
    </div>
  </div>
);

export const Boats = () => {
  const { boats, settings } = useSite();
  const operators = boats?.operators || [];

  return (
    <section id="journey" data-testid="journey-section" className="py-20 sm:py-28 bg-ink text-sand relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow light>Getting Here</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance">
              The journey across, in under an hour
            </h2>
          </div>
          <p className="max-w-sm text-sm text-sand/60 leading-relaxed">
            Fast boats run Sanur ↔ Jungutbatu Beach all day. Pick a company and book your seat straight through
            our concierge on WhatsApp.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="journey-boat-grid">
          {operators.map((o, i) => (
            <Reveal key={o.name} delay={(i % 3) * 0.08}>
              <article
                className="group bg-sand text-ink rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(0,0,0,0.45)]"
                data-testid={`boat-card-${slug(o.name)}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={o.image || DEFAULT_BOAT_IMG}
                    alt={o.image_alt || `${o.name} fast boat Sanur to Nusa Lembongan`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    data-testid={`boat-image-${slug(o.name)}`}
                  />
                  <span className="absolute bottom-3 left-3 bg-ink/80 backdrop-blur text-sand text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1.5 rounded-full" data-testid={`boat-duration-${slug(o.name)}`}>
                    <Ship size={11} className="inline mr-1 -mt-0.5" /> 35–50 min journey
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl leading-tight">{o.name}</h3>
                  <div className="mt-4 space-y-3">
                    <TimeGroup label="Sanur → Lembongan" times={o.to_lembongan} testid={`boat-schedule-out-${slug(o.name)}`} />
                    <TimeGroup label="Lembongan → Sanur" times={o.to_sanur} testid={`boat-schedule-in-${slug(o.name)}`} />
                  </div>
                  <a
                    href={waLink(settings, `Hello Mahadinusa Lembongan, I would like to book a fast boat seat with ${o.name}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`boat-book-now-${slug(o.name)}`}
                    className="mt-5 flex items-center justify-center gap-2 rounded-full bg-azure text-sand text-sm font-medium px-5 py-3 transition-all hover:bg-azure-dark hover:shadow-[0_0_0_5px_rgba(26,98,137,0.16)]"
                  >
                    Book Your Boat <ArrowUpRight size={14} />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-sand/50 font-mono tracking-wide" data-testid="journey-note">
              {boats?.updated_note || "Schedules can change with weather and crossing safety."}
            </p>
            {settings?.boat_source_url && (
              <a
                href={settings.boat_source_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="journey-source-link"
                className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-sand transition-colors"
              >
                Source: Sanur Harbour <ArrowUpRight size={13} />
              </a>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
